/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
'use strict';

import { Component, OnInit } from '@angular/core';
import { LoggerService } from '../../../../modules/shared/services/logger.service';
import { LoginService } from '../../services/login.service';
import { BeginPasswordLogin, LoginFailed, PrepareToLogin } from '../../state/authState.actions';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { AutoLoginConfig } from '../../../../types';
import { LoginPayload, PrepareToLoginPayload } from '../../state/authState.model';
import { AppConfigState } from '../../../state/appConfig/app-config.state';
import { Observable } from 'rxjs';
import { concatMap, concatMapTo, map, take } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthState } from '../../state/authState.state';
import { OAuthService } from 'angular-oauth2-oidc';
import { DashGisUtil } from '../../../../modules/shared/dash-gis-util';
import { OpenIdState } from '../../state/openIdState.state';
import { Navigate } from '@ngxs/router-plugin';

@Component({
    selector: 'dash-gis-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
    @Select(AppConfigState.autoLogin) autoLogin$: Observable<AutoLoginConfig>;
    @Select(AppConfigState.enableRemember) enableRemember$: Observable<boolean>;
    @Select(AuthState.loginMethods) authenticators$: Observable<string[]>;
    openIdButtonText$: Observable<string> = this.store.select(OpenIdState.buttonText);
    loginInProgress$: Observable<boolean> = this.store.select(AuthState.authenticationInProgress);

    needsForgotPassword$: Observable<boolean>;

    loginForm: UntypedFormGroup;

    invalidLogin = false;

    constructor(
        private logger: LoggerService,
        private fb: UntypedFormBuilder,
        private loginService: LoginService,
        private store: Store,
        private actions$: Actions,
        private oauthService: OAuthService
    ) {
        this.loginForm = this.fb.group(
            {
                username: ['', Validators.required],
                password: ['', Validators.required],
                rememberMe: [false],
            },
            { updateOn: 'change' }
        );
    }

    ngOnInit() {
        this.actions$.pipe(ofActionSuccessful(LoginFailed)).subscribe(() => {
            this.invalidLogin = true;
        });

        this.needsForgotPassword$ = this.authenticators$.pipe(
            map((authList) => authList.findIndex((a => a === 'password')) !== -1)
        );
    }

    guestLogin() {
        this.invalidLogin = false;
        const loginPayload$ = this.autoLogin$.pipe(
            take(1),
            map(
                (autoLogin) =>
                    ({
                        credentials: {
                            username: autoLogin.guestuser,
                            password: autoLogin.guestpassword,
                        },
                        remember: false,
                    } as LoginPayload)
            )
        );
        this.store
            .dispatch(new PrepareToLogin({ method: 'password', remember: false }))
            .pipe(
                concatMapTo(loginPayload$),
                concatMap((payload) => this.store.dispatch(new BeginPasswordLogin(payload)))
            )
            .subscribe();
    }

    doLogin() {
        this.invalidLogin = false;
        const enableRemember = this.store.selectSnapshot(AppConfigState.enableRemember);
        const preparePayload: PrepareToLoginPayload = {
            method: 'password',
            remember: enableRemember && this.loginForm.get('rememberMe').value,
        };
        const payload: LoginPayload = {
            credentials: {
                username: this.loginForm.get('username').value,
                password: this.loginForm.get('password').value,
            },
        };
        this.store
            .dispatch(new PrepareToLogin(preparePayload))
            .pipe(concatMapTo(this.store.dispatch(new BeginPasswordLogin(payload))))
            .subscribe();
    }

    oauthLogin() {
        const requested = this.store.selectSnapshot(AuthState.requestedRoute);
        let url = null;
        if (requested?.url) {
            const dummy = 'file://dummy';
            const requestedUrl = new URL(requested.url, dummy);
            if (requested.queryParams) {
                Object.keys(requested.queryParams).forEach((q) =>
                    requestedUrl.searchParams.append(q, requested.queryParams[q])
                );
            }
            url = DashGisUtil.removeSubstring(dummy, requestedUrl.toString());
        }

        const enableRemember = this.store.selectSnapshot(AppConfigState.enableRemember);
        const preparePayload: PrepareToLoginPayload = {
            method: 'oidc',
            remember: enableRemember && this.loginForm.get('rememberMe').value,
        };

        this.store
            .dispatch(new PrepareToLogin(preparePayload))
            .pipe(take(1))
            .subscribe(() => this.oauthService.initLoginFlow(url));
    }

    forgotPassword() {
        this.store.dispatch(new Navigate(['/auth/forgot']));
    }
}
